import { computed, ref } from 'vue'

import { defineStore } from 'pinia'

import { getAdminV2PreviewAccessToken, type AdminV2PreviewEnvelope } from '@common/core-layer/utils/admin-preview-token.utils'

import type { CoursesAdminPreviewContext } from '../utils/courses-admin-preview-url.utils'

/**
 * Courses admin-preview session (clientportal, top-level `/courses` only).
 *
 * Holds non-sensitive preview context (`locationId`, `builder`, `templateId`, `companyId`) plus the agency
 * Bearer token the federated `membership-hmi-preview` remote uses to bootstrap an admin Firebase
 * session via `POST /user/login-token`.
 *
 * Context is set on both server and client for SSR routing guards; the token is only ever set
 * client-side (see {@link setAdminPreviewToken}, called from `InitThemeLayout`) and is kept out
 * of the SSR payload.
 */
export const useCoursesAdminPreviewStore = defineStore('courses-admin-preview-ui', () => {
  const isAdminPreview = ref<boolean>(false)
  const agencyBearerToken = ref<string | null>(null)
  const previewLocationId = ref<string | null>(null)
  const builderMode = ref<boolean>(false)
  const templateId = ref<string | null>(null)
  const companyId = ref<string | null>(null)
  // IAM v2 admin-preview session (the `acatv2` envelope) exchanged via `loginWithAdminTokenV2`.
  const adminSession = ref<AdminV2PreviewEnvelope | null>(null)

  const hasToken = computed(() => !!agencyBearerToken.value)
  const adminAccessToken = computed(() => getAdminV2PreviewAccessToken(adminSession.value))

  function setAdminPreviewContext(context: CoursesAdminPreviewContext) {
    isAdminPreview.value = true
    previewLocationId.value = context.locationId
    builderMode.value = context.builder
    templateId.value = context.templateId
    companyId.value = context.companyId ?? null
  }

  function setAdminPreviewToken(token: string) {
    agencyBearerToken.value = token
  }

  function setAdminSession(session: AdminV2PreviewEnvelope | null) {
    adminSession.value = session
    // Prefer the server-minted location id from the exchange envelope.
    if (session?.locationId) {
      previewLocationId.value = session.locationId
    }
  }

  function clearAdminPreview() {
    isAdminPreview.value = false
    agencyBearerToken.value = null
    previewLocationId.value = null
    builderMode.value = false
    templateId.value = null
    companyId.value = null
    adminSession.value = null
  }

  return {
    // State (computed for reactivity, not readonly)
    isAdminPreview: computed(() => isAdminPreview.value),
    agencyBearerToken: computed(() => agencyBearerToken.value),
    previewLocationId: computed(() => previewLocationId.value),
    builderMode: computed(() => builderMode.value),
    templateId: computed(() => templateId.value),
    companyId: computed(() => companyId.value),
    adminSession: computed(() => adminSession.value),
    hasToken,
    adminAccessToken,

    // Actions
    setAdminPreviewContext,
    setAdminPreviewToken,
    setAdminSession,
    clearAdminPreview,
  }
})
